import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Seo } from "@/components/Seo";
import { ArrowRight, ArrowUpRight, Check, ShoppingCart } from "lucide-react";
import { useCart } from "@/cart";

// Scroll-reveal animations removed (user request 2026-08-26) — renders statically
const FadeUp = ({ children, className = "" }) => (
  <div className={className}>{children}</div>
);

const chapters = [
  {
    num: "01",
    title: "What we do",
    copy: "DISJINX designs and builds AI-powered workflows that take repetitive, time-consuming tasks off your team's plate — permanently. Data entry, follow-ups, reporting, scheduling: if it's busywork, we automate it.",
  },
  {
    num: "02",
    title: "How we do it",
    copy: "We study how your business runs, find the tasks eating your hours, then engineer custom automations that handle them quietly in the background — around the clock, without sick days or mistakes.",
  },
  {
    num: "03",
    title: "Why it matters",
    copy: "Every hour your team spends on busywork is an hour not spent on customers, craft, or growth. We give those hours back — and the money they were costing you.",
  },
];

const plans = [
  {
    name: "Essential",
    weekly: "$49",
    setup: "$49 one-time setup fee",
    blurb: "For small businesses starting with automation.",
    features: ["Up to 3 steps per automation", "Up to 3 app connections", "Weekly monitoring & maintenance", "Standard support", "Usage fee: $0.01 per credit"],
    plan: "essential",
    testId: "pricing-card-essential",
  },
  {
    name: "Advanced",
    weekly: "$79",
    setup: "$99 one-time setup fee",
    blurb: "For businesses automating multiple areas.",
    features: ["Up to 7 steps per automation", "Up to 5 app connections", "Weekly monitoring & maintenance", "Standard support", "Usage fee: $0.005 per credit"],
    featured: true,
    plan: "advanced",
    testId: "pricing-card-advanced",
  },
  {
    name: "Pro",
    weekly: "$149",
    setup: "$199 one-time setup fee",
    blurb: "For businesses with significant automation requirements.",
    features: ["Up to 15 steps per automation", "Unlimited app connections", "Weekly monitoring & maintenance", "Priority support", "Usage fee: $0.003 per credit"],
    plan: "pro",
    testId: "pricing-card-pro",
  },
];

const marqueeWords = ["AUTOMATION", "EFFICIENCY", "DISJINX", "SAVE TIME", "SAVE MONEY", "AI WORKFLOWS"];

export default function Home() {
  const { addItem } = useCart();
  return (
    <main data-testid="home-page">
      <Seo
        title="DISJINX | AI Automation for Australian Businesses"
        description="DISJINX builds AI automations for Australian businesses — automating repetitive, time-consuming tasks to save you hours and money every week. Plans from $49/week with a free one-week trial."
        path="/"
      />
      {/* HERO */}
      <section data-testid="hero-section" className="relative mt-[70px] w-full overflow-hidden">
        <div className="relative aspect-[5/2] w-full">
          <img
            src="/hero-bg-v2.jpg"
            alt="A person tossing paperwork into the air while walking through a sunny field at sunset"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <h1 className="sr-only">Drop the busywork. Reclaim the day.</h1>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            data-testid="hero-headline"
            className="absolute left-[5.8%] top-[2.3%] h-[27%] w-[64%]"
          >
            <svg viewBox="0 0 1115 73" preserveAspectRatio="none" className="block h-full w-full overflow-visible" aria-hidden="true">
              <defs>
                <linearGradient id="heroGradBlack" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0a0a0a" stopOpacity="1" />
                  <stop offset="55%" stopColor="#0a0a0a" stopOpacity="1" />
                  <stop offset="100%" stopColor="#0a0a0a" stopOpacity="0.3" />
                </linearGradient>
              </defs>
              <text
                x="0" y="71" textLength="1115" lengthAdjust="spacingAndGlyphs"
                className="font-display"
                fill="url(#heroGradBlack)"
                style={{ fontSize: 100, fontWeight: 800 }}
              >
                DROP THE BUSYWORK.
              </text>
            </svg>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="absolute left-[5.8%] top-[31.9%] h-[25.2%] w-[49.8%]"
          >
            <svg viewBox="0 0 985 73" preserveAspectRatio="none" className="block h-full w-full overflow-visible" aria-hidden="true">
              <defs>
                <linearGradient id="heroGradGrey" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#525252" stopOpacity="1" />
                  <stop offset="55%" stopColor="#525252" stopOpacity="1" />
                  <stop offset="100%" stopColor="#525252" stopOpacity="0.28" />
                </linearGradient>
              </defs>
              <text
                x="0" y="71" textLength="985" lengthAdjust="spacingAndGlyphs"
                className="font-display"
                fill="url(#heroGradGrey)"
                style={{ fontSize: 100, fontWeight: 800 }}
              >
                RECLAIM THE DAY.
              </text>
            </svg>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="absolute left-[5.6%] top-[57%] flex h-[28%] w-[49.8%] flex-col justify-between"
          >
            <p
              data-testid="hero-subcopy"
              className="text-[1.4vw] font-medium leading-[1.35] text-white"
            >
              DISJINX builds AI workflows and automations for Australian businesses — taking
              repetitive, time-consuming tasks off your plate, saving you serious money, and even more time.
            </p>
            <div className="flex flex-wrap items-center gap-[1vw]">
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="group inline-flex items-center gap-[0.6vw] bg-neutral-950 px-[2.3vw] py-[1.2vw] text-[1.1vw] font-semibold text-white transition-colors duration-200 hover:bg-neutral-800"
              >
                Start a conversation
                <ArrowRight className="h-[1.1vw] w-[1.1vw] transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <a
                href="#pricing"
                data-testid="hero-cta-pricing"
                className="group inline-flex items-center gap-[0.6vw] border border-neutral-950 bg-white px-[2.3vw] py-[1.2vw] text-[1.1vw] font-semibold text-neutral-950 transition-colors duration-200 hover:bg-neutral-100"
              >
                See plans
                <ArrowUpRight className="h-[1.1vw] w-[1.1vw] transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section data-testid="marquee-section" className="border-y border-neutral-200 bg-neutral-50 py-8">
        <Marquee speed={35} gradient={false} pauseOnHover>
          {marqueeWords.map((w) => (
            <span key={w} className="mx-10 font-display text-3xl font-bold tracking-tight text-neutral-200 lg:text-4xl">
              {w} <span className="text-neutral-200">—</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* MANIFESTO */}
      <section data-testid="manifesto-section" className="py-32 lg:py-44">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <FadeUp>
            <p className="mb-20 text-xs font-semibold uppercase tracking-[0.25em] text-neutral-500">The DISJINX manifesto</p>
          </FadeUp>
          <div className="space-y-28">
            {chapters.map((c, i) => (
              <FadeUp key={c.num} delay={i * 0.08}>
                <div data-testid={`manifesto-chapter-${c.num}`} className={`grid gap-8 lg:grid-cols-12 ${["", "lg:pl-24", "lg:pl-48"][i] || ""}`}>
                  <div className="lg:col-span-4">
                    <span className="font-display text-8xl font-light leading-none text-neutral-200 lg:text-9xl">{c.num}</span>
                  </div>
                  <div className="lg:col-span-8 lg:pt-6">
                    <h2 className="font-display text-3xl font-bold tracking-tight lg:text-4xl">{c.title}</h2>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-neutral-600 md:text-lg">{c.copy}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* EXAMPLES — the automation animation */}
      <section data-testid="examples-section" className="border-t border-neutral-200 bg-neutral-50 py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <FadeUp>
            <p className="mb-6 text-xs font-semibold uppercase tracking-[0.25em] text-neutral-500">Examples</p>
            <h2 className="font-display text-4xl font-bold tracking-tight lg:text-5xl">What DISJINX can automate.</h2>
            <p className="mt-6 max-w-xl text-base text-neutral-600 md:text-lg">
              A live look at how a DISJINX workflow runs. Automations like these are examples of what we can
              build for your business — each one quietly takes a repetitive task off your plate and keeps it
              moving in the background, 24/7.
            </p>
          </FadeUp>
          <FadeUp className="mt-16">
            <iframe
              data-testid="hero-animation"
              src="/automation-animation.html?embed=1"
              title="Looping animation of DISJINX automations at work"
              scrolling="no"
              loading="lazy"
              className="pointer-events-none aspect-video w-full border border-neutral-200 bg-white lg:aspect-[21/9]"
            />
          </FadeUp>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" data-testid="pricing-section" className="py-32 lg:py-44">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <FadeUp>
            <p className="mb-6 text-xs font-semibold uppercase tracking-[0.25em] text-neutral-500">Plans &amp; pricing</p>
            <h2 className="font-display text-4xl font-bold tracking-tight lg:text-5xl">Simple plans. Serious savings.</h2>
            <p data-testid="pricing-tagline" className="mt-6 max-w-2xl text-base font-semibold text-neutral-950 md:text-lg">
              Simple, transparent pricing. No hidden fees or long-term contracts.
            </p>
            <p className="mt-4 max-w-2xl text-base text-neutral-600 md:text-lg">
              All prices are in AUD. Each plan covers ONE automation — need more? Add multiple plans to your
              cart and check out in one go, including multiples of the same plan.
            </p>
            <p className="mt-4 max-w-2xl text-base text-neutral-600 md:text-lg">
              On the day of purchase you'll only be charged the Setup fee.
            </p>
            <p data-testid="pricing-no-charge-note" className="mt-3 max-w-2xl text-base font-medium text-neutral-950 md:text-lg">
              You will not be charged the weekly fee until your automation is built and running.
            </p>
          </FadeUp>
          <div className="mt-20 grid gap-px border border-neutral-200 bg-neutral-200 lg:grid-cols-3">
            {plans.map((p, i) => (
              <FadeUp key={p.name} delay={i * 0.1} className="bg-white">
                <div
                  data-testid={p.testId}
                  className={`flex h-full flex-col p-10 lg:p-12 ${p.featured ? "bg-neutral-950 text-white" : ""}`}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-2xl font-bold tracking-tight">{p.name}</h3>
                    {p.featured && (
                      <span className="border border-white/30 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-white/80">
                        Popular
                      </span>
                    )}
                  </div>
                  <div className="mt-10 flex items-baseline gap-2.5">
                    <span className="font-display text-5xl font-extrabold tracking-tight">{p.weekly}</span>
                    <span className={`text-sm ${p.featured ? "text-neutral-400" : "text-neutral-500"}`}>/week</span>
                  </div>
                  <p className={`mt-2 text-sm ${p.featured ? "text-neutral-400" : "text-neutral-500"}`}>{p.setup}</p>
                  <p data-testid={`${p.testId}-note`} className={`mt-1 text-xs font-semibold uppercase tracking-widest ${p.featured ? "text-neutral-300" : "text-neutral-500"}`}>
                    Only setup charged today · weekly starts at launch
                  </p>
                  <p className={`mt-8 text-sm leading-relaxed ${p.featured ? "text-neutral-300" : "text-neutral-600"}`}>{p.blurb}</p>
                  <ul className={`mt-6 flex-1 space-y-2.5 text-sm ${p.featured ? "text-neutral-300" : "text-neutral-600"}`}>
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5">
                        <Check className={`mt-0.5 h-4 w-4 shrink-0 ${p.featured ? "text-white" : "text-neutral-950"}`} strokeWidth={2} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => addItem(p.plan)}
                    data-testid={`${p.testId}-cta`}
                    className={`group mt-10 inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold transition-colors duration-200 ${
                      p.featured
                        ? "bg-white text-neutral-950 hover:bg-neutral-200"
                        : "bg-neutral-950 text-white hover:bg-neutral-800"
                    }`}
                  >
                    <ShoppingCart className="h-4 w-4" />
                    Add {p.name} to cart
                  </button>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.15}>
            <div data-testid="custom-plan-card" className="mt-px grid gap-8 border border-t-0 border-neutral-200 bg-neutral-950 p-10 text-white lg:grid-cols-12 lg:items-center lg:p-12">
              <div className="lg:col-span-8">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-neutral-500">Custom planning</p>
                <h3 className="mt-4 font-display text-2xl font-bold tracking-tight">Need something built around you?</h3>
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-300">
                  More steps, more app connections, or specialised requirements — we'll design a Custom Plan
                  around your exact needs and determine the best pricing we can offer.
                </p>
              </div>
              <div className="lg:col-span-4 lg:text-right">
                <Link
                  to="/contact?topic=custom-plan"
                  data-testid="custom-plan-cta"
                  className="group inline-flex items-center gap-2 bg-white px-6 py-3.5 text-sm font-semibold text-neutral-950 transition-colors duration-200 hover:bg-neutral-200"
                >
                  Enquire about a Custom Plan
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </div>
            <div className="mt-px grid gap-px border border-t-0 border-neutral-200 bg-neutral-200 lg:grid-cols-2">
              <div data-testid="free-trial-card" className="relative overflow-hidden bg-neutral-950 text-white">
                <img
                  src="/free-trial-bg.jpg"
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover object-[72%] sm:object-[80%]"
                />
                {/* Subtle left fade so white text stays readable — photo itself is left untouched */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/45 to-transparent" aria-hidden="true" />
                <div className="relative flex aspect-[3/2] flex-col justify-center p-6 sm:aspect-[2/1] sm:p-10 lg:aspect-auto lg:p-12">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-neutral-300 sm:text-xs">Free trial</p>
                  <h3 className="mt-2 max-w-[12rem] font-display text-lg font-bold tracking-tight sm:mt-4 sm:max-w-sm sm:text-2xl">Try us free for one week</h3>
                  <p className="mt-2 max-w-[12rem] text-xs leading-snug text-neutral-200 sm:mt-4 sm:max-w-xs sm:text-sm sm:leading-relaxed">
                    We'll build a simple, custom automation for your business and run it free for a week —
                    no charge, no commitment.
                  </p>
                  <Link
                    to="/contact?topic=free-trial"
                    data-testid="free-trial-cta"
                    className="group mt-5 inline-flex w-fit items-center gap-2 bg-white px-5 py-3 text-xs font-semibold text-neutral-950 transition-colors duration-200 hover:bg-neutral-200 sm:mt-8 sm:px-6 sm:py-3.5 sm:text-sm"
                  >
                    Enquire about a free trial
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </div>
              </div>
              <div data-testid="unsure-card" className="relative overflow-hidden bg-neutral-950 text-white">
                <img
                  src="/unsure-bg-v2.jpg"
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover object-center"
                />
                {/* Subtle left-only fade so white text stays readable — photo itself is left untouched */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/50 to-transparent" aria-hidden="true" />
                <div className="relative flex aspect-[3/2] flex-col justify-center p-6 sm:aspect-[2/1] sm:p-10 lg:aspect-auto lg:p-12">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-neutral-300 sm:text-xs">Talk to us</p>
                  <h3 className="mt-2 max-w-[12rem] font-display text-lg font-bold tracking-tight sm:mt-4 sm:max-w-sm sm:text-2xl">Still unsure which plan best suits you?</h3>
                  <p className="mt-2 max-w-[12rem] text-xs leading-snug text-neutral-200 sm:mt-4 sm:max-w-xs sm:text-sm sm:leading-relaxed">
                    Tell us about your business and the tasks eating your time — we'll point you to the right fit.
                  </p>
                  <Link
                    to="/contact"
                    data-testid="unsure-cta"
                    className="group mt-5 inline-flex w-fit items-center gap-2 bg-white px-5 py-3 text-xs font-semibold text-neutral-950 transition-colors duration-200 hover:bg-neutral-200 sm:mt-8 sm:px-6 sm:py-3.5 sm:text-sm"
                  >
                    Contact us here
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
      <section data-testid="cta-section" className="px-6 pb-32 lg:px-12">
        <div className="mx-auto max-w-7xl">
          <FadeUp>
            <div className="bg-neutral-950 px-8 py-24 text-white lg:px-24 lg:py-32">
              <h2 className="max-w-3xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight lg:text-6xl">
                Ready to stop doing robot work?
              </h2>
              <p className="mt-8 max-w-xl text-base leading-relaxed text-neutral-400 md:text-lg">
                Tell us what's slowing your business down. We'll show you exactly what an automation could give back — in hours and in dollars.
              </p>
              <Link
                to="/contact"
                data-testid="cta-section-button"
                className="group mt-12 inline-flex items-center gap-2 bg-white px-7 py-4 text-sm font-semibold text-neutral-950 transition-colors duration-200 hover:bg-neutral-200"
              >
                Start a conversation
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
